import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { PackageSearch, CheckCircle2, Circle, Truck, ExternalLink } from "lucide-react";
import { api, money, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get("order_id") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [order, setOrder] = useState(null);
  const [searching, setSearching] = useState(false);

  const lookup = useCallback(async (oid, em) => {
    setSearching(true);
    try {
      const { data } = await api.get(`/orders/track?order_id=${encodeURIComponent(oid)}&email=${encodeURIComponent(em)}`);
      setOrder(data);
    } catch (err) {
      setOrder(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Order not found");
    } finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const oid = params.get("order_id"), em = params.get("email");
    if (oid && em) lookup(oid, em);
  }, [params, lookup]);

  const submit = (e) => {
    e.preventDefault();
    if (!orderId.trim() || !email.trim()) { toast.error("Enter your order number and email"); return; }
    lookup(orderId.trim(), email.trim());
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-14">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#4A5D23] font-mono">Order Status</p>
      <h1 className="font-heading text-4xl md:text-5xl uppercase font-black tracking-tighter mt-2">Track Your Order</h1>
      <p className="text-black/60 mt-3 text-sm">Enter your order number (e.g. SHDN-XXXXXXXX) and the email you used at checkout.</p>

      <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
        <input data-testid="track-order-id-input" value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Order number"
          className="border border-black/20 px-4 py-3.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
        <input data-testid="track-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address"
          className="border border-black/20 px-4 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
        <button data-testid="track-submit-btn" type="submit" disabled={searching}
          className="bg-[#4A5D23] text-white px-8 py-3.5 text-xs font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150 disabled:opacity-50">
          {searching ? "Searching…" : "Track"}
        </button>
      </form>

      {!order && !searching && (
        <div className="mt-14 text-center text-black/30">
          <PackageSearch size={48} className="mx-auto" />
          <p className="text-sm mt-3">Your order status will appear here.</p>
        </div>
      )}

      {order && (
        <div data-testid="track-result" className="mt-10">
          <div className="border border-black/10">
            <div className="bg-[#F4F3F0] px-6 py-4 flex flex-wrap justify-between items-center gap-2">
              <span className="font-mono font-bold tracking-wider">{order.order_id}</span>
              <span data-testid="track-status-badge" className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 ${order.status === "delivered" ? "bg-[#2E8B57] text-white" : order.status === "shipped" ? "bg-[#4A5D23] text-white" : order.status === "awaiting_payment" ? "bg-[#B8860B] text-white" : "bg-[#121212] text-white"}`}>
                {order.status.replace(/_/g, " ")}
              </span>
            </div>
            <div className="p-6">
              <div className="space-y-0">
                {order.timeline.map((step, i) => (
                  <div key={step.key} data-testid={`track-step-${step.key}`} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      {step.done
                        ? <CheckCircle2 size={22} className="text-[#4A5D23] shrink-0" />
                        : <Circle size={22} className="text-black/15 shrink-0" />}
                      {i < order.timeline.length - 1 && <div className={`w-0.5 flex-1 min-h-[28px] ${step.done ? "bg-[#4A5D23]" : "bg-black/10"}`} />}
                    </div>
                    <div className="pb-7">
                      <p className={`text-sm font-bold uppercase tracking-wider ${step.done ? "text-black" : "text-black/45"}`}>{step.label}</p>
                      {step.at && <p className="text-xs text-black/40 font-mono mt-0.5">{new Date(step.at).toLocaleString()}</p>}
                      {step.key === "shipped" && order.tracking_number && (
                        <p className="text-xs text-[#4A5D23] font-bold mt-1">{order.carrier || "Carrier"} · Tracking # <span className="font-mono">{order.tracking_number}</span></p>
                      )}
                      {step.key === "paid" && !step.done && order.payment_method === "usdc" && (
                        <Link to={`/order/${order.order_id}`} className="text-xs text-[#4A5D23] font-bold underline mt-1 inline-block">Complete your USDC payment →</Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-black/10 pt-4 mt-2 flex flex-wrap justify-between items-center gap-3 text-sm">
                <span className="text-black/60">{order.items.length} item{order.items.length > 1 ? "s" : ""} · Ships to {order.shipping_address.city}, {order.shipping_address.state}</span>
                <span className="font-bold">{money(order.total)}</span>
              </div>
              {order.payment_method === "usdc" && order.payment_status === "paid" && order.payment_transaction_id && (
                <a href={`https://basescan.org/tx/${order.payment_transaction_id}`} target="_blank" rel="noreferrer"
                  className="text-xs text-[#4A5D23] font-bold mt-3 inline-flex items-center gap-1">USDC payment on BaseScan <ExternalLink size={11} /></a>
              )}
            </div>
          </div>
          <p className="text-xs text-black/40 mt-4 flex items-center gap-1.5"><Truck size={13} /> Questions about your shipment? <Link to="/contact" className="text-[#4A5D23] font-bold underline">Contact us</Link></p>
        </div>
      )}
    </div>
  );
}
